import { useFormik } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import * as yup from "yup";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { useApiFeedback } from "../../../../app/useApiFeedback";
import StepperButtons from "../../../../common/custom/StepperButtons";
import OtpVerificationProcess from "../../../../common/custom/Verification/OtpVerificationProcess";
import MessageResponse from "../../../../common/feedback/messageResponse";
import { sessions } from "../../../../common/localstorage/SessionStorage";
import {
  GetOtpThunk,
  selectLogin,
  VerifyOtpThunk,
} from "../../../login/loginSlice";
import { SendVerificationCode } from "../../../login/models";
import {
  addPersonalDetailsThunk,
  fetchApplicantByIdentityNumberThunk,
  selectApplicant,
} from "../../ApplicantSlice";
import { ApplicantInitialState, PersonalDetailsDto } from "../../Models";
import { IdentityVerification } from "./IdentityVerification";
import PersonalInfoForm from "./PersonalInfoForm";
import {
  StartOverButton,
  StyledGrid,
  VerificationButton,
} from "./StyledComponents";

interface Props {
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
  activeStep: number;
}

enum ACTION_TYPE {
  showIdVerification = 0,
  showPersonalDetailsForm = 1,
  showOTPVerification = 2,
}
export default function PersonalDetails({ setActiveStep, activeStep }: Props) {
  // State management
  const [isSaveAndClose, setIsSaveAndClose] = useState<boolean>(false);
  const [showVerifyOTP, setShowVerifyOTP] = useState<boolean>(false);
  const [expiryDate, setExpiryDate] = useState<number | null>(null);
  const [hasSearchedId, setHasSearchedId] = useState<boolean>(false);
  const [initialIdSearch, setInitialIdSearch] = useState<boolean>(false);
  const [personalDetails, setPersonalDetails] = useState<PersonalDetailsDto>();
  const [formValues, setFormValues] = useState<PersonalDetailsDto>();
  const [hasRequestedOtp, setHasRequestedOtp] = useState<boolean>(false);
  const [showActionType, setShowActionType] = useState<ACTION_TYPE>(
    ACTION_TYPE.showIdVerification
  );

  // Redux hooks
  const dispatch = useAppDispatch();
  const applicant = useAppSelector(selectApplicant) as ApplicantInitialState;
  const loginSelect = useAppSelector(selectLogin);
  const { feedback, handleApiResponse } = useApiFeedback();

  // Formik configuration
  const formik = useFormik<PersonalDetailsDto>({
    initialValues: formValues || {
      gender: "",
      identityNumber: "",
      name: "",
      race: "",
      surname: "",
      email: "",
      phoneNumber: "",
      isStepCompleted: false,
      applicationId: 0,
      confirmEmail: "",
    },
    validationSchema: yup.object({
      gender: yup.number().required("Gender is required").positive().min(1),
      name: yup
        .string()
        .required("Name is required")
        .min(3, "A name must have 3 or more letters"),
      identityNumber: yup
        .string()
        .required("Identity number is required")
        .matches(/^[0-9]+$/, "Identity number should contain only digits")
        .matches(
          /(([0-9]{2})(0|1)([0-9])([0-3])([0-9]))([ ]?)(([0-9]{4})([ ]?)([0-1][8]([ ]?)[0-9]))/gm,
          "A valid identity number must be 13 digits"
        )
        .min(13, "A valid identity number must be 13 digits")
        .max(13, "A valid identity number must be 13 digits"),
      race: yup.number().required().positive().min(1),
      surname: yup
        .string()
        .required("Surname is Required")
        .min(3, "A surname must have 3 or more letters"),
      email: yup
        .string()
        .required("Email address is required")
        .matches(
          /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/,
          "Email must be in the correct format"
        ),
      confirmEmail: yup
        .string()
        .oneOf([yup.ref("email"), ""], "Email must match")
        .required("Email address is required")
        .matches(
          /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/,
          "Email must be in the correct format"
        ),
      phoneNumber: yup
        .string()
        .required("Phone number is required")
        .min(10, "A valid phone number must be 10 digits")
        .max(10, "A valid phone number must be 10 digits")
        .matches(/^[0-9]+$/, "Phone number should contain only digits"),
    }),
    enableReinitialize: true,
    onSubmit: (values) => {
      // Returning applicants arrive with every field already prefilled, so
      // formik.dirty will be false even though the server still needs to
      // create a brand-new yearly application (and persist any contact info
      // edits they made). Force the submit in that case.
      const mustSubmitForReturningApplicant =
        personalDetails?.isReturningApplicant === true &&
        personalDetails?.hasCurrentYearApplication !== true;

      if (!formik.dirty && !mustSubmitForReturningApplicant) {
        setActiveStep(activeStep + 1);
        return;
      }

      dispatch(addPersonalDetailsThunk(values)).then((resp: any) => {
        handleApiResponse(resp, false);

        // On a successful save, the user has now completed this step and has a
        // current-year application. Immediately kick off the OTP / login flow
        // instead of waiting for a re-render to pick it up.
        const isFulfilled = resp?.type?.includes?.("fulfilled");
        const code = resp?.payload?.validation?.code;
        const savedOk = isFulfilled && (code === 200 || code === 201);

        if (savedOk && !hasRequestedOtp) {
          setHasRequestedOtp(true);
          dispatch(GetOtpThunk(values.identityNumber)).then((data: any) => {
            handleApiResponse(data, false);
          });
        }
      });
    },
  });

  // OTP Verification Formik
  const formikSendCode = useFormik<SendVerificationCode>({
    initialValues: {
      otp: "",
      identityNumber: "",
    },
    validationSchema: yup.object({
      otp: yup.string().required("OTP is required").min(5).max(5),
    }),
    onSubmit: (values) => {
      values.identityNumber = formik.values.identityNumber;
      dispatch(VerifyOtpThunk(values)).then((response: any) => {
        handleApiResponse(response, true);
        if (
          response.payload?.validation.code === 200 ||
          response.payload?.validation.code === 201
        ) {
          sessions.setToken(response.payload.data.token);
          window.location.reload();
        }
      });
    },
  });

  // Effects
  // Drive the view directly off the search result. Three possible outcomes:
  //   A) applicant already has a current-year application (or just saved this
  //      step successfully) -> trigger OTP / login flow.
  //   B) applicant exists from a previous year -> swap to the form and prefill.
  //   C) brand new applicant -> swap to an empty form.
  useEffect(() => {
    // Wait until the user has actually kicked off a search; until then we keep
    // rendering the IdentityVerification step so the form never flashes.
    if (!initialIdSearch) {
      return;
    }

    const details = applicant.applicantCreateState.personalDetails;

    // A) OTP / login flow
    if (
      !hasRequestedOtp &&
      (details?.hasCurrentYearApplication === true ||
        details?.isStepCompleted === true)
    ) {
      setHasRequestedOtp(true);
      dispatch(GetOtpThunk(details!.identityNumber)).then((data: any) => {
        handleApiResponse(data, false);
      });
      return;
    }

    // B) + C) Switch to the Personal Details form.
    setShowActionType(ACTION_TYPE.showPersonalDetailsForm);
    setHasSearchedId(true);

    if (details) {
      // B) Prefill from the returned record and seed confirmEmail so the
      //    "emails match" validator passes on first render. We push straight
      //    into formValues too so formik reinitializes in the SAME render the
      //    form appears - no flicker between "form shows" and "values fill".
      const prefilled = { ...details, confirmEmail: details.email ?? "" };
      setPersonalDetails(prefilled);
      setFormValues(prefilled);
    }
    // C) no data -> keep the form empty; formik's default initialValues apply.
  }, [initialIdSearch, applicant.applicantCreateState.personalDetails]);

  useEffect(() => {
    if (loginSelect.loginState?.expiryDate) {
      setExpiryDate(loginSelect.loginState.expiryDate);
    }

    if (loginSelect.loginState.expiryDate) {
      setShowActionType(ACTION_TYPE.showOTPVerification);
      setShowVerifyOTP(true);
    }
  }, [loginSelect.loginState.expiryDate, feedback]);

  useEffect(() => {
    const id = formik.values.identityNumber;
    if (id.length > 7) {
      const genderIdentifier = parseInt(id.substring(6, 7));
      formik.setFieldValue("gender", genderIdentifier > 4 ? 1 : 2);
    }
  }, [formik.values.identityNumber]);

  useEffect(() => {
    if (applicant.loginState.accessToken?.application?.personalDetails) {
      setPersonalDetails(
        applicant.loginState.accessToken.application.personalDetails
      );
      setShowActionType(ACTION_TYPE.showPersonalDetailsForm);
    }
  }, [applicant.loginState.accessToken]);

  // Push any prefilled / returned details into formik via enableReinitialize.
  useEffect(() => {
    if (personalDetails) {
      setFormValues(personalDetails);
    }
  }, [personalDetails]);

  // Memoized values
  const isLoading = useMemo(
    () =>
      applicant.applicantCreateState.status === "loading" ||
      loginSelect.loginState.status === "loading",
    [applicant.applicantCreateState.status, loginSelect.loginState.status]
  );

  const isStepCompleted = useMemo(
    () => personalDetails?.isStepCompleted || false,
    [personalDetails]
  );

  // Helper functions
  const searchApplicantById = () => {
    if (formik.values.identityNumber && !hasSearchedId) {
      dispatch(
        fetchApplicantByIdentityNumberThunk(formik.values.identityNumber)
      ).then((resp: any) => {
        handleApiResponse(resp, false);
        setInitialIdSearch(true);
      });
    }
  };

  const disableSearchButton = () => {
    if (formik.values.identityNumber === "") return true;
    const idRegex =
      /(([0-9]{2})(0|1)([0-9])([0-3])([0-9]))([ ]?)(([0-9]{4})([ ]?)([0-1][8]([ ]?)[0-9]))/gm;
    return !idRegex.test(formik.values.identityNumber);
  };

  const renderViewBasedOnState = (view: ACTION_TYPE) => {
    switch (view) {
      case 0:
        if (!hasSearchedId && !sessions.getToken()) {
          return (
            <IdentityVerification
              formik={formik}
              onSearch={searchApplicantById}
              disableSearch={disableSearchButton()}
              loading={isLoading}
            />
          );
        }
        break;
      case 1:
        return (
          <>
            <PersonalInfoForm
              formik={formik}
              personalDetails={personalDetails}
            />
            <StyledGrid item xs={12}>
              <StepperButtons
                activeStep={activeStep}
                setActiveStep={setActiveStep}
                formik={formik}
                status={applicant.applicantCreateState.status}
                isDisabled={false}
                setSaveAndClose={setIsSaveAndClose}
              />
            </StyledGrid>
          </>
        );

      case 2:
        if (showVerifyOTP && applicant.applicantCreateState.personalDetails) {
          return (
            <>
              {showVerifyOTP && (
                <>
                  <OtpVerificationProcess
                    identityNumber={formik.values.identityNumber}
                    expiryDate={expiryDate}
                    setExpiryDate={setExpiryDate}
                    formikSendCode={formikSendCode}
                  />

                  <StyledGrid item xs={12}>
                    <VerificationButton
                      color="primary"
                      variant="contained"
                      onClick={() => formikSendCode.handleSubmit()}
                      loading={loginSelect.loginState.status === "loading"}
                      loadingPosition="start"
                    >
                      Verify OTP
                    </VerificationButton>
                    <StartOverButton
                      onClick={() => {
                        window.location.reload();
                      }}
                    >
                      Start over
                    </StartOverButton>
                  </StyledGrid>
                </>
              )}
            </>
          );
        }
        break;
      default:
        break;
    }
  };

  return (
    <>
      <MessageResponse feedback={feedback} />
      <StyledGrid container spacing={2}>
        {renderViewBasedOnState(showActionType)}
      </StyledGrid>
    </>
  );
}
